import React, { useState, useEffect, useRef } from 'react';
import { 
  PlusIcon,
  Cog6ToothIcon,
  PencilIcon,
  TrashIcon,
  LightBulbIcon,
  ListBulletIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  XMarkIcon,
  ClockIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import avialiteService from '../services/avialiteService';
import { resolveFarmerId } from '../services/farmIdentity';
import { useFarm } from '../contexts/FarmContext';
import { useAuth } from '../contexts/AuthContext';
import { IOT_VENDORS } from '../config/iotVendors';
import FarmSelector from '../components/FarmSelector';
import OrganizationSelector from '../components/OrganizationSelector';
import SearchableFarmSelect from '../components/SearchableFarmSelect';
import PageErrorBoundary from '../components/PageErrorBoundary';

const AvialiteContent = () => {
  // Get current user from AuthContext
  const { user } = useAuth();
  
  // Get Avialite vendor config
  const avialiteVendorId = IOT_VENDORS.AVIALITE.vendorId;
  
  // Get real farms from FarmContext (Supabase)
  const { farms: contextFarms, loading: farmsLoading, selectedFarmId, isAdmin, hasOrganizations, hasFarms } = useFarm();
  
  // Transform farms to match expected format
  const farms = contextFarms?.map(f => ({
    id: f.farm_id,
    name: `${f.farm_name || 'Unnamed Farm'}${f.farmsize ? ` (${f.farmsize} hectares)` : ''}`,
    farmName: f.farm_name,
    status: 'Active'
  })) || [];

  const [devices, setDevices] = useState([]);
  const [selectedTab, setSelectedTab] = useState('list');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error' | 'info', message: string }
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [listError, setListError] = useState(null); // why the LED list could not be loaded (null = loaded)
  const [deviceLogs, setDeviceLogs] = useState({}); // { [deviceId]: [logs] }
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedDeviceForLog, setSelectedDeviceForLog] = useState(null);
  const [addLedFarmId, setAddLedFarmId] = useState(''); // For add led form
  const [editLedFarmId, setEditLedFarmId] = useState(''); // For edit led form
  // Farmer id the Add / Edit forms will send for the farm chosen in them: the
  // farm's registered farmer id when the middleware has a farm record, else
  // the logged-in user (see farmIdentity)
  const [addLedFarmerId, setAddLedFarmerId] = useState('');
  const [editLedFarmerId, setEditLedFarmerId] = useState('');

  // True from Apply on the Control tab until the middleware answers (about 2 minutes)
  const [controlPending, setControlPending] = useState(false);

  // Load logs from localStorage on mount
  useEffect(() => {
    const savedLogs = localStorage.getItem('avialite_device_logs');
    if (savedLogs) {
      try {
        setDeviceLogs(JSON.parse(savedLogs));
      } catch (e) {
        console.error('Error loading logs:', e);
      }
    }
  }, []);

  // Helper function to add log entry
  const addLog = (deviceId, operation, request, response, status) => {
    const logEntry = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      operation,
      request,
      response,
      status, // 'success' or 'error'
      // A control result carries the HTTP code in httpStatus and the middleware's
      // status word in status, so the code is read first
      httpStatus: response?.httpStatus || response?.status || null
    };

    setDeviceLogs(prev => {
      const newLogs = {
        ...prev,
        [deviceId]: [...(prev[deviceId] || []), logEntry]
      };
      // Save to localStorage
      localStorage.setItem('avialite_device_logs', JSON.stringify(newLogs));
      return newLogs;
    });
  };

  // Number of the newest list request — only that request may update the screen,
  // so a slow or timed-out older request cannot wipe a list loaded after it.
  const listRequestRef = useRef(0);

  // Helper to fetch devices from middleware API
  const fetchDevices = async (farmIdToFetch) => {
    const requestNo = ++listRequestRef.current;
    try {
      setIsLoading(true);
      const farmId = (!farmIdToFetch || farmIdToFetch === 'all') ? 'ALL' : farmIdToFetch;
      // A farm's LEDs are listed under the farmer id of its middleware farm
      // record (see farmIdentity); without one, and for all farms, under the
      // logged-in user's id
      const farmerId = await resolveFarmerId(farmId, user?.id || '');
      if (requestNo !== listRequestRef.current) return; // a newer list request has taken over
      
      console.log('📋 [Avialite] Fetching devices - farmerId:', farmerId, 'farmId:', farmId);
      const result = await avialiteService.listLEDs(farmId, farmerId);
      if (requestNo !== listRequestRef.current) return; // a newer list request has taken over
      
      if (result.success && Array.isArray(result.data)) {
        const devicesWithFarm = result.data.map(device => ({
          ...device,
          farm: device.farmId
        }));
        setDevices(devicesWithFarm);
        setListError(null);
        console.log(`✅ [Avialite] Loaded ${devicesWithFarm.length} devices`);
      } else {
        // A failed request is not an empty farm — keep the reason so the List tab can say so
        console.warn('⚠️ [Avialite] List response:', result.message);
        setDevices([]);
        setListError(result.message || 'The LED service returned an unexpected reply.');
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      if (requestNo !== listRequestRef.current) return;
      setDevices([]);
      setListError('The LED service returned an unexpected reply.');
    } finally {
      if (requestNo === listRequestRef.current) setIsLoading(false);
    }
  };

  // Fetch devices on mount and when selected farm changes
  useEffect(() => {
    if (user?.id) {
      fetchDevices(selectedFarmId);
    }
  }, [user?.id, selectedFarmId]);

  // The edit form's farm choice belongs to one LED — drop it whenever another LED
  // (or none) is selected or the tab changes, so an abandoned choice is never saved.
  useEffect(() => {
    setEditLedFarmId('');
  }, [selectedDevice?.id, selectedTab]);

  // The read-only Farmer ID boxes of the Add and Edit forms show the id the
  // form will send for the farm chosen in it
  const editFormFarmId = editLedFarmId || selectedDevice?.farmId || '';
  useEffect(() => {
    let cancelled = false;
    // Never another farm's id while this one is looked up
    setAddLedFarmerId('');
    setEditLedFarmerId('');
    resolveFarmerId(addLedFarmId, user?.id || '').then((farmerId) => {
      if (!cancelled) setAddLedFarmerId(farmerId);
    });
    resolveFarmerId(editFormFarmId, user?.id || selectedDevice?.farmerId || '').then((farmerId) => {
      if (!cancelled) setEditLedFarmerId(farmerId);
    });
    return () => { cancelled = true; };
  }, [addLedFarmId, editFormFarmId, user?.id, selectedDevice?.farmerId]);

  // Filter devices based on selected farm from context
  const filteredDevices = selectedFarmId === 'all' ? devices : devices.filter(device => String(device.farm) === String(selectedFarmId));

  // The middleware's LED list carries no on/off or brightness state, so the Control
  // dropdown says so instead of printing blanks
  const ledStateLabel = (device) => {
    const unknown = (value) => value === undefined || value === null || value === '';
    return unknown(device.cmd) && unknown(device.brightness)
      ? '(state not reported)'
      : `(Current: ${device.brightness}/5, ${device.cmd})`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Avialite</h1>
        <div className="flex items-center space-x-4">
          {/* Organization Selector - Admin Only */}
          {hasOrganizations && <OrganizationSelector />}
          {/* Farm Selector */}
          {hasFarms && <FarmSelector />}
        </div>
      </div>

      {/* Tab Navigation - Based on 5 API Endpoints */}
      <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {[
          { id: 'list', name: 'List Led', icon: ListBulletIcon },
          { id: 'add', name: 'Add Led', icon: PlusIcon },
          { id: 'edit', name: 'Edit Led', icon: PencilIcon },
          { id: 'delete', name: 'Delete Led', icon: TrashIcon },
          { id: 'control', name: 'Control Led', icon: Cog6ToothIcon },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setSelectedTab(tab.id)}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${
              selectedTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            <span>{tab.name}</span>
          </button>
        ))}
      </div>

      {/* Content based on selected tab */}

      {/* Global feedback banner — success/error from edit/delete/list actions was
          previously rendered only inside the Add tab, so users never saw it. The
          Add tab keeps its own inline copy; render here for every other tab. */}
      {feedback && selectedTab !== 'add' && (
        <div className={`rounded-lg p-4 mb-4 ${
          feedback.type === 'success'
            ? 'bg-green-50 border border-green-200'
            : feedback.type === 'info'
              ? 'bg-blue-50 border border-blue-200'
              : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center">
            {feedback.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
            ) : feedback.type === 'info' ? (
              <ClockIcon className="h-5 w-5 text-blue-500 mr-3" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-500 mr-3" />
            )}
            <p className={`text-sm font-medium ${
              feedback.type === 'success' ? 'text-green-800' : feedback.type === 'info' ? 'text-blue-800' : 'text-red-800'
            }`}>
              {feedback.message}
            </p>
          </div>
        </div>
      )}

      {/* List Led Tab - API 2 */}
      {selectedTab === 'list' && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    LED ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Station ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Note
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gateway ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Farm ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-3 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading devices from API...
                      </div>
                    </td>
                  </tr>
                ) : listError ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center" role="alert">
                      <XCircleIcon className="h-12 w-12 mx-auto text-red-400 mb-3" />
                      <p className="text-sm font-medium text-red-800">Could not load the LED list.</p>
                      <p className="text-sm text-red-700 mt-1">
                        The LED service did not answer or returned an error, so the list is not shown. Please reload the page to try again.
                      </p>
                      <p className="text-xs text-gray-500 mt-2 break-words">Details: {listError}</p>
                    </td>
                  </tr>
                ) : filteredDevices.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      <LightBulbIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                      <p className="text-sm">No LED devices found. Click "Add Led" to register your first device.</p>
                    </td>
                  </tr>
                ) : (
                  filteredDevices.map((device) => (
                    <tr key={device.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <LightBulbIcon className="h-5 w-5 text-yellow-500 mr-3" />
                          <div className="text-sm font-medium text-gray-900">{device.ledId}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {device.stationId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                        {device.note}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {device.gatewayId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {device.farmId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setSelectedDeviceForLog(device);
                              setShowLogModal(true);
                            }}
                            className="text-purple-600 hover:text-purple-900 relative"
                            title="View API Logs"
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                            {deviceLogs[device.id]?.length > 0 && (
                              <span className="absolute -top-1 -right-1 h-3 w-3 bg-purple-500 rounded-full text-[8px] text-white flex items-center justify-center">
                                {deviceLogs[device.id].length}
                              </span>
                            )}
                          </button>
                          <button
                            onClick={() => { setSelectedDevice(device); setSelectedTab('edit'); }}
                            className="text-green-600 hover:text-green-900"
                            title="Edit Led"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => { setSelectedDevice(device); setSelectedTab('control'); }}
                            className="text-blue-600 hover:text-blue-900"
                            title="Control Led"
                          >
                            <Cog6ToothIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => { setSelectedDevice(device); setSelectedTab('delete'); }}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Led"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">API Response Format</h4>
            <div className="text-xs text-blue-700 space-y-1">
              <div>• <strong>ledId:</strong> LED identifier</div>
              <div>• <strong>stationId:</strong> Station identifier</div>
              <div>• <strong>note:</strong> LED description/note</div>
              <div>• <strong>id:</strong> Unique device ID</div>
            </div>
          </div>
        </div>
      )}

      {/* Add Led Tab - API 1 */}
      {selectedTab === 'add' && (
        <div className="max-w-2xl mx-auto">
          <div className="card">
            <div className="text-center mb-6">
              <LightBulbIcon className="mx-auto h-12 w-12 text-yellow-500" />
              <h3 className="text-xl font-semibold text-gray-900 mt-3">Add New LED Device</h3>
              <p className="text-sm text-gray-500 mt-2">Register a new LED to the Avialite system</p>
            </div>
            
            {/* Feedback Message (the Control tab's "waiting" notice is not an Add result) */}
            {feedback && feedback.type !== 'info' && (
              <div className={`rounded-lg p-4 mb-6 ${
                feedback.type === 'success' 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center">
                  {feedback.type === 'success' ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-red-500 mr-3" />
                  )}
                  <p className={`text-sm font-medium ${
                    feedback.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {feedback.message}
                  </p>
                </div>
              </div>
            )}
            
            <form className="space-y-6" onSubmit={async (e) => {
              e.preventDefault();
              setIsSubmitting(true);
              setFeedback(null);
              
              const formData = new FormData(e.target);
              const farmId = addLedFarmId || formData.get('farmId');
              const ledData = {
                ledId: formData.get('ledId'),
                stationId: formData.get('stationId'),
                gatewayId: formData.get('gatewayId'),
                note: formData.get('note'),
                // The farm's registered farmer id (its middleware record), else the logged-in user
                farmerId: await resolveFarmerId(farmId, user?.id || ''),
                farmId,
              };
              
              try {
                // Call the real Kapitani API
                const result = await avialiteService.registerLED(ledData);
                
                if (result.success) {
                  // Add log entry
                  const deviceId = result.data?.id || ledData.ledId;
                  addLog(deviceId, 'REGISTER', ledData, result.data, 'success');
                  
                  // Show success feedback
                  setFeedback({ 
                    type: 'success', 
                    message: result.message || 'LED registered successfully!'
                  });
                  
                  // Refresh the device list from API
                  await fetchDevices(selectedFarmId);
                  
                  // Reset form and switch to list after 2 seconds
                  setTimeout(() => {
                    e.target.reset();
                    setAddLedFarmId('');
                    setFeedback(null);
                    setSelectedTab('list');
                  }, 2000);
                } else {
                  // Add error log
                  addLog(ledData.ledId, 'REGISTER', ledData, result, 'error');
                  
                  // Show error feedback
                  setFeedback({ 
                    type: 'error', 
                    message: result.message || 'Failed to register LED'
                  });
                }
              } catch (error) {
                // Add error log
                addLog(ledData.ledId, 'REGISTER', ledData, { error: error.message }, 'error');
                
                setFeedback({ 
                  type: 'error', 
                  message: error.message || 'An error occurred while registering LED'
                });
              } finally {
                setIsSubmitting(false);
              }
            }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Farmer ID - Auto-filled from current user, not editable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    Farmer ID
                    <LockClosedIcon className="h-4 w-4 ml-1 text-gray-400" title="Auto-filled" />
                  </label>
                  <input 
                    type="text" 
                    name="farmerId"
                    value={addLedFarmerId || user?.id || ''}
                    className="input bg-gray-100 cursor-not-allowed"
                    readOnly
                    title="Auto-filled from your account"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-filled from your account</p>
                </div>

                {/* Vendor ID - Auto-filled from config, not editable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    Vendor ID
                    <LockClosedIcon className="h-4 w-4 ml-1 text-gray-400" title="Auto-filled" />
                  </label>
                  <input 
                    type="text" 
                    name="vendorId"
                    value={avialiteVendorId}
                    className="input bg-gray-100 cursor-not-allowed"
                    readOnly
                    title="Auto-filled from system configuration"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-filled from system config</p>
                </div>

                {/* Farm ID - Dropdown from existing farms, no "All" option */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Farm ID *
                  </label>
                  <SearchableFarmSelect
                    farms={farms}
                    value={addLedFarmId}
                    onChange={(val) => setAddLedFarmId(val)}
                    name="farmId"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Select from your registered farms</p>
                </div>

                {/* LED ID - User input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    LED ID *
                  </label>
                  <input 
                    type="text" 
                    name="ledId"
                    className="input"
                    placeholder="e.g., LED005"
                    required
                  />
                </div>

                {/* Gateway ID - User input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gateway ID *
                  </label>
                  <input 
                    type="text" 
                    name="gatewayId"
                    className="input"
                    placeholder="e.g., GW004"
                    required
                  />
                </div>

                {/* Station ID - User input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Station ID *
                  </label>
                  <input 
                    type="text" 
                    name="stationId"
                    className="input"
                    placeholder="e.g., STA005"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note *
                </label>
                <textarea 
                  name="note"
                  className="input"
                  rows="3"
                  placeholder="Describe the LED location and purpose..."
                  required
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">API Response Format</h4>
                <div className="text-xs text-blue-700 space-y-1">
                  <div>• <strong>status:</strong> Success/Failure status</div>
                  <div>• <strong>id:</strong> Generated LED ID</div>
                  <div>• <strong>timeStamp:</strong> Registration timestamp</div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setSelectedTab('list')}
                  className="flex-1 btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Registering...
                    </span>
                  ) : (
                    'Add LED Device'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Led Tab - API 3 */}
      {selectedTab === 'edit' && (
        <>
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Edit LED Device</h2>
            <div className="text-sm text-gray-500">Select a LED to edit its information</div>
          </div>

          {/* LED Selection */}
          <div className="card">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select LED Device to Edit
            </label>
            <select 
              className="input"
              value={selectedDevice?.id || ''}
              onChange={(e) => {
                const device = filteredDevices.find(d => d.id === e.target.value);
                setSelectedDevice(device);
              }}
            >
              <option value="">Choose a LED device...</option>
              {filteredDevices.map(device => (
                <option key={device.id} value={device.id}>
                  {device.ledId} - {device.note}
                </option>
              ))}
            </select>
          </div>

          {/* Edit Form */}
          {selectedDevice && (
            <div className="max-w-2xl mx-auto">
              <div className="card">
                <div className="text-center mb-6">
                  <PencilIcon className="mx-auto h-12 w-12 text-green-500" />
                  <h3 className="text-xl font-semibold text-gray-900 mt-3">Edit LED: {selectedDevice.ledId}</h3>
                  <p className="text-sm text-gray-500 mt-2">Update LED device information</p>
                </div>
                
                {/* key: rebuild the form for each LED so every box shows the selected LED's values */}
                <form key={selectedDevice.id} className="space-y-6" onSubmit={async (e) => {
                  e.preventDefault();
                  setIsSubmitting(true);
                  
                  const formData = new FormData(e.target);
                  const farmId = editLedFarmId || formData.get('farmId');
                  const editData = {
                    id: selectedDevice.id,
                    ledId: formData.get('ledId'),
                    stationId: formData.get('stationId'),
                    gatewayId: formData.get('gatewayId'),
                    note: formData.get('note'),
                    // The farm's registered farmer id (its middleware record), else the current user
                    farmerId: await resolveFarmerId(farmId, user?.id || selectedDevice.farmerId),
                    farmId,
                    vendorId: avialiteVendorId // Use config vendorId
                  };
                  
                  try {
                    const result = await avialiteService.editLED(editData);
                    
                    if (result.success) {
                      // Add log entry
                      addLog(selectedDevice.id, 'EDIT', editData, result.data, 'success');
                      
                      setFeedback({ type: 'success', message: 'LED updated successfully!' });
                      
                      // Refresh device list
                      await fetchDevices(selectedFarmId);
                      
                      setTimeout(() => {
                        setSelectedDevice(null);
                        setSelectedTab('list');
                        setFeedback(null);
                      }, 2000);
                    } else {
                      // Add error log
                      addLog(selectedDevice.id, 'EDIT', editData, result, 'error');
                      setFeedback({ type: 'error', message: result.message || 'Failed to update LED' });
                    }
                  } catch (error) {
                    // Add error log
                    addLog(selectedDevice.id, 'EDIT', editData, { error: error.message }, 'error');
                    setFeedback({ type: 'error', message: error.message || 'Error updating LED' });
                  } finally {
                    setIsSubmitting(false);
                  }
                }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Farmer ID - Auto-filled, readonly */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        Farmer ID
                        <LockClosedIcon className="h-4 w-4 ml-1 text-gray-400" title="Auto-filled" />
                      </label>
                      <input 
                        type="text" 
                        name="farmerId"
                        value={editLedFarmerId || user?.id || selectedDevice.farmerId}
                        className="input bg-gray-100 cursor-not-allowed"
                        readOnly
                      />
                      <p className="text-xs text-gray-500 mt-1">Auto-filled from your account</p>
                    </div>

                    {/* Vendor ID - Auto-filled, readonly */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        Vendor ID
                        <LockClosedIcon className="h-4 w-4 ml-1 text-gray-400" title="Auto-filled" />
                      </label>
                      <input 
                        type="text" 
                        name="vendorId"
                        value={avialiteVendorId}
                        className="input bg-gray-100 cursor-not-allowed"
                        readOnly
                      />
                      <p className="text-xs text-gray-500 mt-1">Auto-filled from system config</p>
                    </div>

                    {/* Farm ID - Dropdown without "All Farms" */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Farm ID *
                      </label>
                      <SearchableFarmSelect
                        farms={farms}
                        value={editLedFarmId || selectedDevice.farmId}
                        onChange={(val) => setEditLedFarmId(val)}
                        name="farmId"
                        required
                      />
                    </div>

                    {/* LED ID - Editable */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        LED ID *
                      </label>
                      <input 
                        type="text" 
                        name="ledId"
                        defaultValue={selectedDevice.ledId}
                        className="input"
                        required
                      />
                    </div>

                    {/* Gateway ID - Editable */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gateway ID *
                      </label>
                      <input 
                        type="text" 
                        name="gatewayId"
                        defaultValue={selectedDevice.gatewayId}
                        className="input"
                        required
                      />
                    </div>

                    {/* Station ID - Editable */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Station ID *
                      </label>
                      <input 
                        type="text" 
                        name="stationId"
                        defaultValue={selectedDevice.stationId}
                        className="input"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Note *
                    </label>
                    <textarea 
                      name="note"
                      defaultValue={selectedDevice.note}
                      className="input"
                      rows="3"
                      required
                    />
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-green-900 mb-2">API Method: PATCH</h4>
                    <div className="text-xs text-green-700 space-y-1">
                      <div>• <strong>Response:</strong> status</div>
                      <div>• Updates existing LED device information</div>
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedDevice(null);
                        setSelectedTab('list');
                      }}
                      className="flex-1 btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 btn-primary"
                    >
                      Update LED Device
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {!selectedDevice && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-green-900 mb-2">API Method: PATCH</h4>
              <div className="text-xs text-green-700">
                <div>• <strong>Response:</strong> status</div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Led Tab - API 4 */}
      {selectedTab === 'delete' && (
        <>
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Delete LED Device</h2>
            <div className="text-sm text-gray-500">Select a LED to remove from system</div>
          </div>

          {/* LED Selection */}
          <div className="card">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select LED Device to Delete
            </label>
            <select 
              className="input"
              onChange={(e) => {
                const device = filteredDevices.find(d => d.id === e.target.value);
                setSelectedDevice(device);
              }}
            >
              <option value="">Choose a LED device...</option>
              {filteredDevices.map(device => (
                <option key={device.id} value={device.id}>
                  {device.ledId} - {device.note}
                </option>
              ))}
            </select>
          </div>

          {/* Delete Confirmation */}
          {selectedDevice && (
            <div className="max-w-2xl mx-auto">
              <div className="card border-red-200">
                <div className="text-center mb-6">
                  <TrashIcon className="mx-auto h-12 w-12 text-red-500" />
                  <h3 className="text-xl font-semibold text-gray-900 mt-3">Delete LED Device</h3>
                  <p className="text-sm text-red-600 mt-2">This action cannot be undone</p>
                </div>
                
                {/* Device Details */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Device Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">LED ID:</span>
                      <span className="font-medium ml-2">{selectedDevice.ledId}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Station ID:</span>
                      <span className="font-medium ml-2">{selectedDevice.stationId}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Gateway ID:</span>
                      <span className="font-medium ml-2">{selectedDevice.gatewayId}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Farm ID:</span>
                      <span className="font-medium ml-2">{selectedDevice.farmId}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-600">Note:</span>
                      <span className="font-medium ml-2">{selectedDevice.note}</span>
                    </div>
                  </div>
                </div>
                
                <form className="space-y-6" onSubmit={async (e) => {
                  e.preventDefault();
                  const confirmation = window.confirm(
                    `Are you sure you want to delete LED device "${selectedDevice.ledId}"? This action cannot be undone.`
                  );
                  
                  if (confirmation) {
                    setIsSubmitting(true);
                    
                    const deleteData = {
                      id: selectedDevice.id,
                      // The farm's registered farmer id (its middleware record), else the current user
                      farmerId: await resolveFarmerId(selectedDevice.farmId, user?.id || selectedDevice.farmerId),
                      farmId: selectedDevice.farmId
                    };
                    
                    try {
                      const result = await avialiteService.deleteLED(deleteData);
                      
                      if (result.success) {
                        // Add log entry
                        addLog(selectedDevice.id, 'DELETE', deleteData, result.data, 'success');
                        
                        setFeedback({ type: 'success', message: 'LED deleted successfully!' });
                        
                        // Refresh device list
                        await fetchDevices(selectedFarmId);
                        
                        setTimeout(() => {
                          setSelectedDevice(null);
                          setSelectedTab('list');
                          setFeedback(null);
                        }, 2000);
                      } else {
                        // Add error log
                        addLog(selectedDevice.id, 'DELETE', deleteData, result, 'error');
                        setFeedback({ type: 'error', message: result.message || 'Failed to delete LED' });
                      }
                    } catch (error) {
                      // Add error log
                      addLog(selectedDevice.id, 'DELETE', deleteData, { error: error.message }, 'error');
                      setFeedback({ type: 'error', message: error.message || 'Error deleting LED' });
                    } finally {
                      setIsSubmitting(false);
                    }
                  }
                }}>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-red-900 mb-2">API Method: DELETE</h4>
                    <div className="text-xs text-red-700 space-y-1">
                      <div>• <strong>Required Fields:</strong> farmerId, farmId, vendorId, id</div>
                      <div>• <strong>Response:</strong> status</div>
                      <div>• Permanently removes LED device from system</div>
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedDevice(null);
                        setSelectedTab('list');
                      }}
                      className="flex-1 btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      Delete LED Device
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {!selectedDevice && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-red-900 mb-2">API Method: DELETE</h4>
              <div className="text-xs text-red-700">
                <div>• <strong>Fields:</strong> farmerId, farmId, vendorId, id</div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Control Led Tab - API 5 */}
      {selectedTab === 'control' && (
        <>
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Control LED Device</h2>
            <div className="text-sm text-gray-500">Adjust LED brightness and commands</div>
          </div>

          {/* No Avialite device has answered a control command yet (verified 2 Oct 2026);
              commands are still sent so the middleware receives and logs them. */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              LED control: commands are delivered to the Kapitani middleware; no Avialite device
              has confirmed a command yet (Innates/Avialite are checking the channel).
            </p>
          </div>

          {/* LED Selection */}
          <div className="card">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select LED Device to Control
            </label>
            <select 
              className="input"
              onChange={(e) => {
                const device = filteredDevices.find(d => d.id === e.target.value);
                setSelectedDevice(device);
              }}
            >
              <option value="">Choose a LED device...</option>
              {filteredDevices.map(device => (
                <option key={device.id} value={device.id}>
                  {device.ledId} - {device.note} {ledStateLabel(device)}
                </option>
              ))}
            </select>
          </div>

          {/* Control Interface */}
          {selectedDevice && (
            <div className="max-w-2xl mx-auto">
              <div className="card">
                <div className="text-center mb-6">
                  <Cog6ToothIcon className="mx-auto h-12 w-12 text-purple-500" />
                  <h3 className="text-xl font-semibold text-gray-900 mt-3">Control LED: {selectedDevice.ledId}</h3>
                  <p className="text-sm text-gray-500 mt-2">Adjust brightness and control commands</p>
                </div>
                
                {/* Current Status */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Current Status</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm text-center">
                    <div>
                      <div className="text-gray-600">Current Command</div>
                      <div className={`font-medium text-lg ${
                        selectedDevice.cmd === 'ON' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {selectedDevice.cmd}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">Brightness Level</div>
                      <div className="font-medium text-lg text-blue-600">
                        {selectedDevice.brightness}/5
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">Gateway</div>
                      <div className="font-medium text-lg text-purple-600">
                        {selectedDevice.gatewayId}
                      </div>
                    </div>
                  </div>
                </div>
                
                <form className="space-y-6" onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const cmd = formData.get('cmd');
                  const brightness = parseInt(formData.get('brightness'));
                  const device = selectedDevice;
                  // Send the REAL control request. The middleware publishes it on its
                  // channel and waits up to ~120 s for an Avialite device to confirm;
                  // the local on/off/brightness state changes only once a device confirms.
                  const sentAt = new Date().toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit', hour12: false });
                  setControlPending(true);
                  setFeedback({ type: 'info', message: `Command sent to the middleware at ${sentAt}. Waiting for the light to confirm (up to 2 minutes).` });
                  const result = await avialiteService.controlLED(
                    device.ledId, device.stationId, device.gatewayId, cmd, brightness
                  );
                  addLog(device.id, 'CONTROL', { cmd, brightness }, result, result.confirmed ? 'success' : 'error');
                  if (result.confirmed) {
                    const updatedDevice = {
                      ...device,
                      brightness,
                      cmd,
                      state: cmd === 'ON' ? 'On' : 'Off'
                    };
                    // Functional updates: the list and the selection may have changed during the wait
                    setDevices(prev => prev.map(d => (d.id === device.id ? updatedDevice : d)));
                    setSelectedDevice(prev => (prev?.id === device.id ? updatedDevice : prev));
                    setFeedback({ type: 'success', message: 'The light confirmed the command.' });
                  } else if (result.delivered) {
                    setFeedback({ type: 'error', message: 'The middleware delivered the command on its channel, but no Avialite device confirmed it.' });
                  } else {
                    setFeedback({ type: 'error', message: `The command could not be delivered to the middleware: ${String(result.message).replace(/\.$/, '')}.` });
                  }
                  setControlPending(false);
                }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Command *
                      </label>
                      <select
                        name="cmd"
                        className="input"
                        defaultValue={selectedDevice.cmd}
                        required
                      >
                        <option value="ON">ON</option>
                        <option value="OFF">OFF</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Brightness Level: {selectedDevice.brightness}
                      </label>
                      <input
                        type="range"
                        name="brightness"
                        min="0"
                        max="5"
                        defaultValue={selectedDevice.brightness}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0</span>
                        <span>5</span>
                      </div>
                    </div>
                  </div>

                  {/* Device Identification (Read-only) */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Device Identification</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">LED ID:</span>
                        <span className="font-medium ml-2">{selectedDevice.ledId}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Station ID:</span>
                        <span className="font-medium ml-2">{selectedDevice.stationId}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Gateway ID:</span>
                        <span className="font-medium ml-2">{selectedDevice.gatewayId}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-purple-900 mb-2">API Method: POST</h4>
                    <div className="text-xs text-purple-700 space-y-1">
                      <div>• <strong>Required Fields:</strong> ledId, stationId, gatewayId</div>
                      <div>• <strong>Control Fields:</strong> cmd, brightness</div>
                      <div>• <strong>Response:</strong> status</div>
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedDevice(null);
                        setSelectedTab('list');
                      }}
                      className="flex-1 btn-secondary"
                    >
                      Back to List
                    </button>
                    <button
                      type="submit"
                      className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={controlPending}
                    >
                      {controlPending ? 'Waiting for confirmation...' : 'Apply Control Settings'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {!selectedDevice && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-purple-900 mb-2">API Method: POST</h4>
              <div className="text-xs text-purple-700 space-y-1">
                <div>• <strong>Fields:</strong> ledId, stationId, gatewayId</div>
                <div>• <strong>cmd, brightness</strong></div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Log Modal */}
      {showLogModal && selectedDeviceForLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <DocumentTextIcon className="h-6 w-6 mr-2 text-purple-600" />
                  API Activity Log
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Device: <span className="font-semibold">{selectedDeviceForLog.ledId}</span> • 
                  {deviceLogs[selectedDeviceForLog.id]?.length || 0} entries
                </p>
              </div>
              <button
                onClick={() => {
                  setShowLogModal(false);
                  setSelectedDeviceForLog(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {(!deviceLogs[selectedDeviceForLog.id] || deviceLogs[selectedDeviceForLog.id].length === 0) ? (
                <div className="text-center py-12">
                  <DocumentTextIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No API activity recorded for this device yet.</p>
                  <p className="text-sm text-gray-400 mt-2">Logs will appear here when you perform operations.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {[...deviceLogs[selectedDeviceForLog.id]].reverse().map((log, index) => (
                    <div 
                      key={log.id} 
                      className={`border rounded-lg p-4 ${
                        log.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                      }`}
                    >
                      {/* Log Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            log.status === 'success' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {log.operation}
                          </span>
                          {log.status === 'success' ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                          ) : (
                            <XCircleIcon className="h-5 w-5 text-red-600" />
                          )}
                          <span className={`text-sm font-medium ${
                            log.status === 'success' ? 'text-green-900' : 'text-red-900'
                          }`}>
                            {log.status === 'success' ? 'Success' : 'Failed'}
                          </span>
                        </div>
                        <div className="flex items-center text-xs text-gray-500">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {new Date(log.timestamp).toLocaleString('en-MY', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </div>
                      </div>

                      {/* HTTP Status */}
                      {log.httpStatus && (
                        <div className="mb-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-mono ${
                            log.httpStatus === 200 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            HTTP {log.httpStatus}
                          </span>
                        </div>
                      )}

                      {/* Request Section */}
                      <div className="mb-3">
                        <div className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded mr-2">REQUEST</span>
                        </div>
                        <div className="bg-white rounded border border-gray-200 p-3 overflow-x-auto">
                          <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap">
                            {JSON.stringify(log.request, null, 2)}
                          </pre>
                        </div>
                      </div>

                      {/* Response Section */}
                      <div>
                        <div className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                          <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded mr-2">RESPONSE</span>
                        </div>
                        <div className="bg-white rounded border border-gray-200 p-3 overflow-x-auto">
                          <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap">
                            {JSON.stringify(log.response, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t p-4 bg-gray-50 flex justify-between items-center">
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to clear all logs for this device?')) {
                    setDeviceLogs(prev => {
                      const newLogs = { ...prev };
                      delete newLogs[selectedDeviceForLog.id];
                      localStorage.setItem('avialite_device_logs', JSON.stringify(newLogs));
                      return newLogs;
                    });
                  }
                }}
                className="px-4 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
              >
                Clear Logs
              </button>
              <button
                onClick={() => {
                  setShowLogModal(false);
                  setSelectedDeviceForLog(null);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// A render error anywhere on the Avialite page shows a "Something went wrong"
// card with a Reload button instead of a blank screen.
const Avialite = () => (
  <PageErrorBoundary pageName="Avialite">
    <AvialiteContent />
  </PageErrorBoundary>
);

export default Avialite;
